import { useMemo, useState } from "react";
import {
  Search,
  Filter,
  CheckCircle2,
  AlertCircle,
  Clock,
  MoreHorizontal,
  Receipt,
  Download,
  Upload,
  FileText,
  Pencil,
  Trash2,
  Inbox as InboxIcon,
  X,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Subscriptions } from "./Subscriptions";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useReceipts } from "../../lib/receipts/useReceipts";
import { useVendors } from "../../lib/vendors/useVendors";
import type { Expense, ExpenseStatus } from "../../lib/expenses/types";
import { formatAmountCents, formatIsoDate } from "../../lib/format";
import { NewExpenseDialog } from "../components/NewExpenseDialog";
import { ImportCsvDialog } from "../components/ImportCsvDialog";
import { VendorLogo } from "../components/VendorLogo";
import { ScopePinnedBadge } from "../components/ScopePinnedBadge";
import { useAuth } from "../../lib/auth/useAuth";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useEffectiveScopeForTab } from "../../lib/scope";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const STATUS_OPTIONS: { value: ExpenseStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "needs_info", label: "Needs info" },
  { value: "processing", label: "Processing" },
  { value: "ready", label: "Ready" },
];

interface FilterState {
  statuses: ExpenseStatus[];
  fromDate: string;
  toDate: string;
}

const EMPTY_FILTER: FilterState = {
  statuses: [],
  fromDate: "",
  toDate: "",
};

function StatusBadge({ status }: { status: ExpenseStatus }) {
  switch (status) {
    case "ready":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-lg border border-green-100">
          <CheckCircle2 className="w-3.5 h-3.5" /> Ready
        </span>
      );
    case "needs_info":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg border border-amber-100">
          <AlertCircle className="w-3.5 h-3.5" /> Needs info
        </span>
      );
    case "processing":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg border border-blue-100">
          <Receipt className="w-3.5 h-3.5" /> Processing
        </span>
      );
    case "draft":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-600 rounded-lg border border-slate-200">
          <Clock className="w-3.5 h-3.5" /> Draft
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-500 rounded-lg border border-slate-200">
          Archived
        </span>
      );
  }
}

function matchesFilter(expense: Expense, filter: FilterState): boolean {
  if (filter.statuses.length > 0 && !filter.statuses.includes(expense.status)) {
    return false;
  }
  if (filter.fromDate && expense.occurredOn < filter.fromDate) return false;
  if (filter.toDate && expense.occurredOn > filter.toDate) return false;
  return true;
}

// Wave 22 — apply the effective scope to the expense list. "mine" restricts
// to rows the current user either created OR submitted; "company" leaves
// the list untouched. Checking both `createdBy` and `submittedBy` is the
// fix for wave-25-post: drafts have submittedBy=null until they're
// submitted, so scoping on submittedBy alone hid the creator's own unsubmitted
// rows from ExpensesHub (bug report: "I see my drafts in Inbox but not here").
// Null userId (auth still hydrating) collapses to empty to avoid flashing
// company rows before auth lands.
function applyScope(
  expenses: Expense[],
  scope: "mine" | "company",
  userId: string | null,
): Expense[] {
  if (scope === "company") return expenses;
  if (!userId) return [];
  return expenses.filter(
    (e) => e.createdBy === userId || e.submittedBy === userId,
  );
}

export function ExpensesHub() {
  const navigate = useNavigate();
  const { expenses, remove } = useExpenses();
  const { receipts } = useReceipts();
  const { getById: getVendor } = useVendors();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { scope, pinned } = useEffectiveScopeForTab("expenses");
  const [searchParams] = useSearchParams();
  const canWrite = can("write:expenses_own") || can("write:expenses_any");
  // Query-string driven filters. Phase 5's Audit Dashboard links here with
  // `?filter=missingReceipt`. The subscriptions tab is also deep-linkable
  // via `?tab=subscriptions`.
  const initialTab: "all" | "subscriptions" =
    searchParams.get("tab") === "subscriptions" ? "subscriptions" : "all";
  const missingReceiptOnly = searchParams.get("filter") === "missingReceipt";
  const [activeTab, setActiveTab] = useState<"all" | "subscriptions">(
    initialTab,
  );

  const [search, setSearch] = useState("");
  // Committed filter (applied to the list) vs. draft filter (popover state).
  const [appliedFilter, setAppliedFilter] =
    useState<FilterState>(EMPTY_FILTER);
  const [draftFilter, setDraftFilter] = useState<FilterState>(EMPTY_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);

  const [editExpense, setEditExpense] = useState<Expense | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState<number | null>(null);

  // Only show non-archived expenses — the localStorage store returns everything
  // so we filter at the page level.
  const visibleExpenses = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const expenseIdsWithReceipt = new Set(
      receipts
        .map((r) => r.linkedExpenseId)
        .filter((x): x is string => Boolean(x)),
    );
    const scoped = applyScope(expenses, scope, user?.id ?? null);
    return scoped
      .filter((e) => e.status !== "archived")
      .filter((e) => {
        if (!missingReceiptOnly) return true;
        return !expenseIdsWithReceipt.has(e.id);
      })
      .filter((e) => {
        if (!needle) return true;
        return e.vendor.toLowerCase().includes(needle);
      })
      .filter((e) => matchesFilter(e, appliedFilter));
  }, [expenses, receipts, search, appliedFilter, missingReceiptOnly, scope, user?.id]);

  const filterActive =
    appliedFilter.statuses.length > 0 ||
    appliedFilter.fromDate !== "" ||
    appliedFilter.toDate !== "";

  function handleFilterOpenChange(next: boolean): void {
    if (next) {
      // Seed the draft from the applied filter each time the popover opens so
      // closing without Apply discards any in-progress edits.
      setDraftFilter(appliedFilter);
    }
    setFilterOpen(next);
  }

  function toggleDraftStatus(status: ExpenseStatus): void {
    setDraftFilter((prev) => {
      const has = prev.statuses.includes(status);
      return {
        ...prev,
        statuses: has
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      };
    });
  }

  function applyDraftFilter(): void {
    setAppliedFilter(draftFilter);
    setFilterOpen(false);
  }

  function clearDraftFilter(): void {
    setDraftFilter(EMPTY_FILTER);
    setAppliedFilter(EMPTY_FILTER);
  }

  async function handleDelete(expense: Expense): Promise<void> {
    const ok = window.confirm(
      `Delete expense "${expense.vendor}"? This can't be undone.`,
    );
    if (!ok) return;
    await remove(expense.id);
  }

  const editInitialValues = editExpense
    ? {
        vendor: editExpense.vendor,
        occurredOn: editExpense.occurredOn,
        amountCents: editExpense.amountCents,
        categoryId: editExpense.categoryId,
        categoryLabel: editExpense.categoryLabel ?? null,
        taxPolicyId: editExpense.taxPolicyId,
        taxPolicyLabel: editExpense.taxPolicyLabel ?? null,
        businessPurpose: editExpense.businessPurpose ?? undefined,
      }
    : undefined;

  return (
    <div
      className="h-full flex flex-col gap-6 max-w-6xl mx-auto"
      data-scope={scope}
      data-testid="expenses-hub-root"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">Expenses</h1>
            {pinned && <ScopePinnedBadge forcedScope={scope} />}
          </div>
          <p className="text-slate-500 mt-1">
            Track and manage all your team expenses
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <button
              type="button"
              data-testid="expenses-hub-import-csv"
              onClick={() => setImportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl shadow-sm transition-colors text-sm"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
          )}
          <button
            type="button"
            data-testid="expenses-hub-export"
            onClick={() => navigate("/exports?prefill=true")}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl shadow-sm transition-colors text-sm"
          >
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {/* Import success banner */}
      {importSuccessCount !== null && (
        <div
          data-testid="expenses-hub-import-success"
          className="flex items-center justify-between gap-3 px-4 py-2 bg-green-50 border border-green-200 text-green-800 text-sm rounded-xl"
        >
          <span>
            Successfully imported {importSuccessCount} expense
            {importSuccessCount !== 1 ? "s" : ""}.
          </span>
          <button
            type="button"
            onClick={() => setImportSuccessCount(null)}
            className="p-1 text-green-700 hover:text-green-900 hover:bg-green-100 rounded"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center border-b border-slate-200">
        <button
          onClick={() => setActiveTab("all")}
          data-testid="expenses-hub-tab-all"
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "all"
              ? "border-[#0070F2] text-[#0070F2]"
              : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
          }`}
        >
          All Expenses
        </button>
        <button
          onClick={() => setActiveTab("subscriptions")}
          data-testid="expenses-hub-tab-subscriptions"
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "subscriptions"
              ? "border-[#0070F2] text-[#0070F2]"
              : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
          }`}
        >
          Subscriptions
        </button>
      </div>

      {activeTab === "all" ? (
        <div className="flex flex-col gap-4 flex-1">
          {missingReceiptOnly && (
            <div
              data-testid="expenses-hub-missing-receipt-banner"
              className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border border-amber-100 text-amber-800 text-sm rounded-xl"
            >
              <span>Showing only expenses without a receipt attached.</span>
              <button
                type="button"
                onClick={() => navigate("/expenses")}
                className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline"
              >
                Clear filter
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="expenses-hub-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search expenses..."
                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition-shadow"
              />
            </div>
            <Popover open={filterOpen} onOpenChange={handleFilterOpenChange}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="expenses-hub-filter-button"
                  className={`flex items-center gap-2 px-4 py-2 font-medium rounded-xl shadow-sm transition-colors text-sm border ${
                    filterActive
                      ? "text-blue-700 bg-blue-50 border-blue-200"
                      : "text-slate-600 bg-white border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <Filter className="w-4 h-4" /> Filter
                  {filterActive && (
                    <span className="inline-flex items-center justify-center text-[10px] font-semibold h-4 min-w-4 px-1 rounded-full bg-blue-600 text-white">
                      {appliedFilter.statuses.length +
                        (appliedFilter.fromDate ? 1 : 0) +
                        (appliedFilter.toDate ? 1 : 0)}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                data-testid="expenses-hub-filter-popover"
                className="w-80"
              >
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                      Status
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {STATUS_OPTIONS.map((opt) => {
                        const checked = draftFilter.statuses.includes(
                          opt.value,
                        );
                        return (
                          <label
                            key={opt.value}
                            className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              data-testid={`expenses-hub-filter-status-${opt.value}`}
                              checked={checked}
                              onChange={() => toggleDraftStatus(opt.value)}
                              className="rounded border-slate-300 text-[#0070F2] focus:ring-[#0070F2]/30"
                            />
                            {opt.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                      Date range
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex flex-col gap-1 text-xs text-slate-600">
                        From
                        <input
                          type="date"
                          data-testid="expenses-hub-filter-from"
                          value={draftFilter.fromDate}
                          onChange={(e) =>
                            setDraftFilter((prev) => ({
                              ...prev,
                              fromDate: e.target.value,
                            }))
                          }
                          className="px-2 py-1.5 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs text-slate-600">
                        To
                        <input
                          type="date"
                          data-testid="expenses-hub-filter-to"
                          value={draftFilter.toDate}
                          onChange={(e) =>
                            setDraftFilter((prev) => ({
                              ...prev,
                              toDate: e.target.value,
                            }))
                          }
                          className="px-2 py-1.5 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                    <button
                      type="button"
                      data-testid="expenses-hub-filter-clear"
                      onClick={clearDraftFilter}
                      className="text-xs font-medium text-slate-600 hover:text-slate-900"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      data-testid="expenses-hub-filter-apply"
                      onClick={applyDraftFilter}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-[#0070F2] hover:bg-[#005BCA] rounded-md"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
            {visibleExpenses.length === 0 ? (
              <div
                data-testid="expenses-hub-empty-state"
                className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-12 text-slate-500"
              >
                <InboxIcon className="w-10 h-10 text-slate-300" />
                <div className="text-sm font-medium text-slate-700">
                  {expenses.length === 0
                    ? "No expenses yet"
                    : "No expenses match your filters"}
                </div>
                <div className="text-xs text-slate-500">
                  {expenses.length === 0
                    ? "Create one from the top bar to get started."
                    : "Try clearing filters or widening the date range."}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Merchant
                      </th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleExpenses.map((expense) => {
                      const categoryLabel =
                        expense.categoryLabel?.trim() || "Uncategorized";
                      return (
                        <tr
                          key={expense.id}
                          data-testid={`expense-row-${expense.id}`}
                          className="hover:bg-slate-50 transition-colors group"
                        >
                          <td className="px-6 py-4">
                            <Link
                              to={`/expenses/${expense.id}`}
                              className="block"
                            >
                              <div className="font-semibold text-slate-900 flex items-center gap-2">
                                {expense.vendorId &&
                                  (() => {
                                    const v = getVendor(expense.vendorId);
                                    return v ? (
                                      <VendorLogo vendor={v} size={20} />
                                    ) : null;
                                  })()}
                                {expense.vendor}
                              </div>
                              <div className="text-sm text-slate-500">
                                {categoryLabel} •{" "}
                                {formatIsoDate(expense.occurredOn)}
                              </div>
                            </Link>
                          </td>
                          <td className="px-6 py-4">
                            <Link
                              to={`/expenses/${expense.id}`}
                              className="block"
                            >
                              <div className="font-semibold text-slate-900">
                                {formatAmountCents(
                                  expense.amountCents,
                                  expense.currency,
                                )}
                              </div>
                            </Link>
                          </td>
                          <td className="px-6 py-4">
                            <Link
                              to={`/expenses/${expense.id}`}
                              className="block"
                            >
                              <StatusBadge status={expense.status} />
                            </Link>
                          </td>
                          <td className="px-6 py-4">
                            <Link
                              to={`/expenses/${expense.id}`}
                              className="block text-sm font-medium text-slate-700"
                            >
                              {expense.employee ?? "—"}
                            </Link>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  data-testid={`expense-row-more-${expense.id}`}
                                  aria-label={`Actions for ${expense.vendor}`}
                                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                                >
                                  <MoreHorizontal className="w-5 h-5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canWrite && (
                                  <DropdownMenuItem
                                    data-testid={`expense-row-edit-${expense.id}`}
                                    onSelect={() => setEditExpense(expense)}
                                  >
                                    <Pencil className="w-4 h-4" /> Edit
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  data-testid={`expense-row-view-${expense.id}`}
                                  onSelect={() =>
                                    navigate(`/expenses/${expense.id}`)
                                  }
                                >
                                  <FileText className="w-4 h-4" /> View detail
                                </DropdownMenuItem>
                                {canWrite && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      data-testid={`expense-row-delete-${expense.id}`}
                                      variant="destructive"
                                      onSelect={() => {
                                        void handleDelete(expense);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" /> Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 -mt-6">
          <Subscriptions />
        </div>
      )}

      {editExpense && (
        <NewExpenseDialog
          open={editExpense !== null}
          onOpenChange={(next) => {
            if (!next) setEditExpense(null);
          }}
          mode="edit"
          expenseId={editExpense.id}
          initialValues={editInitialValues}
        />
      )}

      <ImportCsvDialog
        mode="expense"
        open={importOpen}
        onOpenChange={setImportOpen}
        onSuccess={(count) => {
          setImportOpen(false);
          setImportSuccessCount(count);
        }}
      />
    </div>
  );
}
